import { useMemo } from 'react';

/**
 * GeometricCover - Data-Driven SVG Cover Art Generator
 * Strictly uses the story's `level` property to determine theme
 * 
 * Level 1: Green background / White Circle
 * Level 2: Yellow background / White Triangle
 * Level 3: Blue background / White Square
 */

const LEVEL_THEMES = {
  '1': {
    name: 'Level 1',
    bgColor: '#22c55e',      // Green-500
    bgGradient: ['#16a34a', '#22c55e'], // Green gradient
    shapeColor: 'white',
    textColor: 'white',
    shape: 'circle'
  },
  '2': {
    name: 'Level 2',
    bgColor: '#eab308',      // Yellow-500
    bgGradient: ['#ca8a04', '#eab308'], // Yellow gradient
    shapeColor: 'white',
    textColor: 'white',
    shape: 'triangle'
  },
  '3': {
    name: 'Level 3',
    bgColor: '#3b82f6',      // Blue-500
    bgGradient: ['#2563eb', '#3b82f6'], // Blue gradient
    shapeColor: 'white',
    textColor: 'white',
    shape: 'square'
  }
};

// Truncate text to fit within bounds
function truncateText(text, maxLength) {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

export function GeometricCover({ 
  title = 'Untitled', 
  author = 'Unknown Author',
  level = '1', 
  className = ''
}) {
  // Get theme based on level - defaults to Level 1 if invalid
  const theme = LEVEL_THEMES[String(level)] || LEVEL_THEMES['1'];
  
  // Generate unique gradient ID based on title
  const gradientId = useMemo(() => {
    return `cover-gradient-${title.replace(/\s+/g, '-').slice(0, 20)}`;
  }, [title]);

  // Render the shape based on level
  const renderShape = () => {
    const centerX = 75;
    const centerY = 55;
    const size = 35;
    
    switch (theme.shape) {
      case 'circle':
        return (
          <circle
            cx={centerX}
            cy={centerY}
            r={size}
            fill={theme.shapeColor}
            opacity="0.95"
          />
        );
      
      case 'triangle':
        // Equilateral triangle pointing up
        const h = size * 1.732; // height = side * sqrt(3)
        const points = `${centerX},${centerY - size} ${centerX - size},${centerY + size * 0.6} ${centerX + size},${centerY + size * 0.6}`;
        return (
          <polygon
            points={points}
            fill={theme.shapeColor}
            opacity="0.95"
          />
        );
      
      case 'square':
        return (
          <rect
            x={centerX - size}
            y={centerY - size}
            width={size * 2}
            height={size * 2}
            fill={theme.shapeColor}
            opacity="0.95"
            rx={4}
          />
        );
      
      default:
        return null;
    }
  };

  // Truncate title and author for display
  const displayTitle = truncateText(title, 25);
  const displayAuthor = truncateText(author, 30);

  return (
    <svg
      viewBox="0 0 150 200"
      className={className}
      style={{ width: '100%', height: '100%' }}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={`Book cover for ${title} by ${author}`}
    >
      {/* Gradient Definition */}
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={theme.bgGradient[0]} />
          <stop offset="100%" stopColor={theme.bgGradient[1]} />
        </linearGradient>
      </defs>
      
      {/* Background with gradient */}
      <rect 
        width="150" 
        height="200" 
        fill={`url(#${gradientId})`}
        rx="8"
      />
      
      {/* Subtle pattern overlay */}
      <rect 
        width="150" 
        height="200" 
        fill="url(#noise)" 
        opacity="0.03"
        rx="8"
      />
      
      {/* Level indicator badge */}
      <rect
        x="10"
        y="10"
        width="40"
        height="20"
        rx="10"
        fill="rgba(0,0,0,0.2)"
      />
      <text
        x="30"
        y="24"
        textAnchor="middle"
        fill="white"
        fontSize="10"
        fontWeight="600"
        fontFamily="system-ui, -apple-system, sans-serif"
      >
        Lv.{level}
      </text>
      
      {/* Central shape (white) */}
      {renderShape()}
      
      {/* Title - Centered below shape */}
      <text
        x="75"
        y="125"
        textAnchor="middle"
        fill={theme.textColor}
        fontSize="11"
        fontWeight="700"
        fontFamily="system-ui, -apple-system, sans-serif"
      >
        {displayTitle.length > 15 ? (
          <>
            <tspan x="75" dy="0">{displayTitle.slice(0, 15)}</tspan>
            <tspan x="75" dy="13">{displayTitle.slice(15)}</tspan>
          </>
        ) : (
          displayTitle
        )}
      </text>
      
      {/* Author - Centered below title */}
      <text
        x="75"
        y={displayTitle.length > 15 ? 155 : 145}
        textAnchor="middle"
        fill="rgba(255,255,255,0.85)"
        fontSize="9"
        fontWeight="400"
        fontFamily="system-ui, -apple-system, sans-serif"
      >
        {displayAuthor}
      </text>
      
      {/* Bottom decorative line */}
      <rect
        x="40"
        y="175"
        width="70"
        height="3"
        rx="1.5"
        fill="rgba(255,255,255,0.3)"
      />
    </svg>
  );
}

export default GeometricCover;
